import React from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { Ruler } from 'lucide-react';

interface LabelSizePopoverProps {
  labelWidth: number;
  labelHeight: number;
  onLabelSizeChange: (width: number, height: number) => void;
}

const LABEL_PRESETS = [
  { name: 'Standard', width: 50.8, height: 25.4 },
  { name: 'Address', width: 89, height: 36 },
  { name: 'Small', width: 62, height: 29 },
  { name: 'Shipping', width: 100, height: 50 },
  { name: 'Tiny', width: 40, height: 20 },
  { name: 'Medium', width: 70, height: 35 },
  { name: 'A4', width: 210, height: 297 },
];

export const LabelSizePopover: React.FC<LabelSizePopoverProps> = ({
  labelWidth,
  labelHeight,
  onLabelSizeChange,
}) => {
  const [customWidth, setCustomWidth] = React.useState(labelWidth.toString());
  const [customHeight, setCustomHeight] = React.useState(labelHeight.toString());
  const [open, setOpen] = React.useState(false);

  React.useEffect(() => {
    setCustomWidth(labelWidth.toString());
    setCustomHeight(labelHeight.toString());
  }, [labelWidth, labelHeight]);

  const handleCustomSizeApply = () => {
    const w = parseFloat(customWidth);
    const h = parseFloat(customHeight);
    if (w > 0 && h > 0 && w <= 300 && h <= 300) {
      onLabelSizeChange(w, h);
      setOpen(false);
    }
  };

  const handlePresetClick = (width: number, height: number) => {
    onLabelSizeChange(width, height);
    setOpen(false);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          className="gap-1.5 h-8 px-2 sm:px-3"
        >
          <Ruler className="w-3.5 h-3.5" />
          <span className="text-xs">{labelWidth} × {labelHeight}</span>
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-64 p-3" align="center">
        <div className="space-y-3">
          <Label className="text-xs font-medium">Label Size (mm)</Label>
          
          {/* Presets */}
          <div className="grid grid-cols-2 gap-1.5">
            {LABEL_PRESETS.map((preset) => (
              <Button
                key={preset.name}
                variant={labelWidth === preset.width && labelHeight === preset.height ? 'default' : 'outline'}
                size="sm"
                className="h-auto py-1.5 px-2 text-left justify-start"
                onClick={() => handlePresetClick(preset.width, preset.height)}
              >
                <div>
                  <div className="text-[10px] font-medium">{preset.width} × {preset.height}</div>
                  <div className="text-[9px] text-muted-foreground">{preset.name}</div>
                </div>
              </Button>
            ))}
          </div>

          {/* Custom Size */}
          <div className="pt-2 border-t border-border">
            <Label className="text-[10px] text-muted-foreground mb-1.5 block">Custom</Label>
            <div className="flex items-center gap-1.5">
              <Input
                type="number"
                value={customWidth}
                onChange={(e) => setCustomWidth(e.target.value)}
                placeholder="W"
                className="h-7 text-xs"
                min={10}
                max={300}
              />
              <span className="text-muted-foreground text-xs">×</span>
              <Input
                type="number"
                value={customHeight}
                onChange={(e) => setCustomHeight(e.target.value)}
                placeholder="H"
                className="h-7 text-xs"
                min={10}
                max={300}
              />
              <Button size="sm" onClick={handleCustomSizeApply} className="h-7 px-2 text-xs">
                Apply
              </Button>
            </div>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
};
